import {
  notificationSourceMeta,
  severityMeta,
  filterTypeMeta,
  allNotificationSources,
  allFilterTypes,
  type NotificationSource,
  type NotificationSeverity,
  type NotificationFilterType,
} from '@/mocks/saGlobalNotificationCenter';

interface NotificationFiltersProps {
  searchTerm: string;
  onSearchChange: (v: string) => void;
  sourceFilter: NotificationSource | 'all';
  onSourceFilterChange: (v: NotificationSource | 'all') => void;
  severityFilter: NotificationSeverity | 'all';
  onSeverityFilterChange: (v: NotificationSeverity | 'all') => void;
  filterTypeFilter: NotificationFilterType | 'all';
  onFilterTypeFilterChange: (v: NotificationFilterType | 'all') => void;
  listTab: 'all' | 'unread' | 'important' | 'archived';
  onListTabChange: (v: 'all' | 'unread' | 'important' | 'archived') => void;
  sortBy: 'newest' | 'oldest' | 'priority' | 'source';
  onSortByChange: (v: 'newest' | 'oldest' | 'priority' | 'source') => void;
  totalCount: number;
  unreadCount: number;
  importantCount: number;
  archivedCount: number;
  sourceCounts: Record<string, number>;
  filteredCount: number;
  onClearFilters: () => void;
}

export default function NotificationFilters({
  searchTerm,
  onSearchChange,
  sourceFilter,
  onSourceFilterChange,
  severityFilter,
  onSeverityFilterChange,
  filterTypeFilter,
  onFilterTypeFilterChange,
  listTab,
  onListTabChange,
  sortBy,
  onSortByChange,
  totalCount,
  unreadCount,
  importantCount,
  archivedCount,
  sourceCounts,
  filteredCount,
  onClearFilters,
}: NotificationFiltersProps) {
  const hasFilters = sourceFilter !== 'all' || severityFilter !== 'all' || filterTypeFilter !== 'all' || searchTerm.trim() !== '' || listTab !== 'all';

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200 p-3 md:p-4 space-y-3">
      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar notificaciones..."
            className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs placeholder:text-foreground-400 outline-none focus:border-foreground-400 transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
            >
              <i className="ri-close-line text-foreground-400 text-[10px]"></i>
            </button>
          )}
        </div>

        {/* Sort dropdown */}
        <div className="relative flex-shrink-0">
          <select
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value as typeof sortBy)}
            className="appearance-none w-full sm:w-36 pl-3.5 pr-8 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs font-medium outline-none cursor-pointer focus:border-foreground-400 transition-colors"
          >
            <option value="newest">Más recientes</option>
            <option value="oldest">Más antiguas</option>
            <option value="priority">Por prioridad</option>
            <option value="source">Por fuente</option>
          </select>
          <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
        </div>
      </div>

      {/* Tab pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none pb-0.5">
        <button
          onClick={() => onListTabChange('all')}
          className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
            listTab === 'all' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
          }`}
        >
          Todas ({totalCount})
        </button>
        <button
          onClick={() => onListTabChange('unread')}
          className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
            listTab === 'unread' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
          }`}
        >
          No leídas ({unreadCount})
        </button>
        <button
          onClick={() => onListTabChange('important')}
          className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
            listTab === 'important' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
          }`}
        >
          <i className="ri-star-fill text-[9px]"></i>
          Importantes ({importantCount})
        </button>
        <button
          onClick={() => onListTabChange('archived')}
          className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
            listTab === 'archived' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
          }`}
        >
          <i className="ri-archive-line text-[9px]"></i>
          Archivadas ({archivedCount})
        </button>

        <span className="w-px h-5 bg-background-200 shrink-0 mx-0.5"></span>

        {/* Severity pills */}
        {(['critical', 'error', 'warning', 'info'] as NotificationSeverity[]).map((sev) => {
          const meta = severityMeta[sev];
          const isActive = severityFilter === sev;
          return (
            <button
              key={sev}
              onClick={() => onSeverityFilterChange(severityFilter === sev ? 'all' : sev)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                isActive ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <i className={`${meta.icon} text-[9px]`}></i>
              {meta.label}
            </button>
          );
        })}
      </div>

      {/* Source chips */}
      <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none pb-0.5">
        <button
          onClick={() => onSourceFilterChange('all')}
          className={`shrink-0 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
            sourceFilter === 'all' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
          }`}
        >
          Todas las fuentes
        </button>
        {allNotificationSources.map((src) => {
          const meta = notificationSourceMeta[src];
          const count = sourceCounts[src] || 0;
          const isActive = sourceFilter === src;
          if (listTab === 'archived' && count === 0) return null;
          return (
            <button
              key={src}
              onClick={() => onSourceFilterChange(sourceFilter === src ? 'all' : src)}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                isActive ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <i className={`${meta.icon} text-[10px]`}></i>
              {meta.label}
            </button>
          );
        })}
      </div>

      {/* Filter type chips */}
      <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none pb-0.5">
        {allFilterTypes.map((ft) => {
          const meta = filterTypeMeta[ft];
          const isActive = filterTypeFilter === ft;
          return (
            <button
              key={ft}
              onClick={() => onFilterTypeFilterChange(filterTypeFilter === ft ? 'all' : ft)}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                isActive ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <i className={`${meta.icon} text-[10px]`}></i>
              {meta.label}
            </button>
          );
        })}
      </div>

      {/* Results + clear */}
      {hasFilters && (
        <div className="flex items-center justify-between pt-2 border-t border-background-100">
          <span className="text-foreground-400 text-[10px]">{filteredCount} resultado(s)</span>
          <button
            onClick={onClearFilters}
            className="text-foreground-400 text-[10px] font-medium hover:text-foreground-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-go-back-line text-[10px] mr-1"></i>
            Limpiar filtros
          </button>
        </div>
      )}
    </div>
  );
}